import { CsBrandKit } from '../../../common/models/content-studio.model';
import { VendorProfile } from '../../../common/hooks/use-vendor-profile-api.hook';
import { BrandFieldKey } from './brand-nodes';

/**
 * How much of the brand we actually know.
 *
 * The kit was previously all-or-nothing: it either existed or it did not, and
 * a vendor whose discovery run found a palette and nothing else saw the same
 * finished-looking page as one we knew inside out. That is the worst kind of
 * quiet failure — the agent goes on to write captions from a bio, and the only
 * clue is that the output is generic.
 *
 * So: a figure, and — more usefully than the figure — what is missing and what
 * would fix it. "62%" on its own is a score. "62%, and connecting Instagram is
 * the biggest thing left" is an instruction.
 *
 * Weighted by what the agent actually needs rather than by field count. Their
 * own sentences change what it writes more than a secondary colour does, so
 * they are worth more; counting fields equally would let a kit reach 90% while
 * missing the one thing that matters.
 */

export type BrandGapKind = 'field' | 'connection';

export interface BrandGap {
    key: string;
    /** What is missing, in the vendor's words. */
    label: string;
    /** How much of the total this is worth, so the list can be ordered. */
    weight: number;
    kind: BrandGapKind;
    /** The panel to open, when the vendor can simply type it. */
    field?: BrandFieldKey;
    /** The platform to connect, when they cannot. */
    platform?: string;
}

export interface BrandCompleteness {
    /** 0–100, rounded. */
    percent: number;
    /** Everything still missing, heaviest first. */
    gaps: BrandGap[];
    /** Whether anything at all is known. */
    started: boolean;
}

interface Check {
    key: string;
    labelKey: string;
    weight: number;
    kind: BrandGapKind;
    field?: BrandFieldKey;
    platform?: string;
    has: (kit: CsBrandKit | null, profile: VendorProfile, connected: string[]) => boolean;
}

const filled = (v?: string) => !!v?.trim();
const any = (v?: unknown[]) => Array.isArray(v) && v.length > 0;

/**
 * The weights are a product judgement, written down.
 *
 * Their own sentences and their photography are what the agent leans on hardest
 * and what it cannot fake; a palette is quick to read and quick to fix by hand.
 * A connected account is weighted like a field because that is exactly what it
 * is worth — it is the only source of the captions above it.
 */
const CHECKS: Check[] = [
    {
        key: 'writingExamples', labelKey: 'BRAND.GAP.WRITING_EXAMPLES', weight: 16,
        kind: 'field', field: 'toneOfVoice',
        has: (kit) => any(kit?.writingExamples),
    },
    {
        key: 'socialConnected', labelKey: 'BRAND.GAP.SOCIAL', weight: 14,
        kind: 'connection', platform: 'instagram',
        // The one gap a vendor cannot close by typing. Without it there are no
        // captions, and captions are the best writing evidence there is.
        has: (_kit, _profile, connected) => connected.length > 0,
    },
    {
        key: 'referenceImageUrls', labelKey: 'BRAND.GAP.REFERENCES', weight: 12,
        kind: 'field', field: 'referenceImageUrls',
        has: (kit) => any(kit?.referenceImageUrls),
    },
    {
        key: 'visualStyle', labelKey: 'BRAND.GAP.VISUAL_STYLE', weight: 10,
        kind: 'field', field: 'visualStyle',
        has: (kit) => filled(kit?.visualStyle),
    },
    {
        key: 'toneOfVoice', labelKey: 'BRAND.GAP.TONE', weight: 9,
        kind: 'field', field: 'toneOfVoice',
        has: (kit) => filled(kit?.toneOfVoice),
    },
    {
        key: 'primaryColors', labelKey: 'BRAND.GAP.COLOURS', weight: 8,
        kind: 'field', field: 'primaryColors',
        has: (kit) => any(kit?.primaryColors),
    },
    {
        key: 'proofPoints', labelKey: 'BRAND.GAP.PROOF_POINTS', weight: 8,
        kind: 'field', field: 'houseRules',
        has: (kit) => any(kit?.proofPoints),
    },
    {
        key: 'audience', labelKey: 'BRAND.GAP.AUDIENCE', weight: 7,
        kind: 'field', field: 'houseRules',
        has: (kit) => filled(kit?.audience),
    },
    {
        key: 'styleKeywords', labelKey: 'BRAND.GAP.STYLE', weight: 6,
        kind: 'field', field: 'styleKeywords',
        has: (kit) => any(kit?.styleKeywords),
    },
    {
        key: 'primaryFont', labelKey: 'BRAND.GAP.FONT', weight: 5,
        kind: 'field', field: 'primaryFont',
        has: (kit) => filled(kit?.primaryFont),
    },
    {
        key: 'brandName', labelKey: 'BRAND.GAP.NAME', weight: 3,
        kind: 'field', field: 'brandName',
        has: (kit) => filled(kit?.brandName),
    },
    {
        key: 'houseRules', labelKey: 'BRAND.GAP.HOUSE_RULES', weight: 2,
        kind: 'field', field: 'houseRules',
        has: (_kit, profile) => filled(profile.houseRules),
    },
];

export const TOTAL_WEIGHT = CHECKS.reduce((sum, c) => sum + c.weight, 0);

export function brandCompleteness(
    kit: CsBrandKit | null,
    profile: VendorProfile,
    /** Platforms with a live connection. */
    connected: string[],
    translate: (key: string) => string,
): BrandCompleteness {
    let earned = 0;
    const gaps: BrandGap[] = [];

    for (const check of CHECKS) {
        if (check.has(kit, profile, connected)) {
            earned += check.weight;
        } else {
            gaps.push({
                key: check.key,
                label: translate(check.labelKey),
                weight: check.weight,
                kind: check.kind,
                field: check.field,
                platform: check.platform,
            });
        }
    }

    return {
        // Rounded, but never rounded UP to 100 while a gap remains: "100%"
        // beside a list of missing things is the page contradicting itself.
        percent: gaps.length === 0 ? 100 : Math.min(99, Math.round((earned / TOTAL_WEIGHT) * 100)),
        gaps: gaps.sort((a, b) => b.weight - a.weight),
        started: earned > 0,
    };
}

/**
 * Connected, but connected after the brand kit was last read.
 *
 * The connection gap closes the moment an account is linked, which is correct
 * — but it makes the bar say the brand is fully known while the kit has never
 * seen a single one of their captions. The evidence is sitting there unread,
 * and nothing on the page says so.
 *
 * Deliberately not a weighted gap. Nothing is missing; what is missing is a
 * re-read, which costs credits and is therefore the vendor's decision rather
 * than something to dock them points for.
 */
export function socialReadPending(
    kit: { lastExtractedAt?: string | null } | null | undefined,
    connected: Array<{ connectedAt?: string }>,
): boolean {
    // Never read at all is a different prompt — the page already offers the
    // first read, and saying both at once is two buttons for one job.
    if (!kit?.lastExtractedAt || !connected.length) return false;

    const readAt = new Date(kit.lastExtractedAt).getTime();
    if (!Number.isFinite(readAt)) return false;

    return connected.some((account) => {
        const at = new Date(account.connectedAt ?? '').getTime();
        return Number.isFinite(at) && at > readAt;
    });
}
